/**
 * Wraps the backend / ajax lib for better centralizing
 * common interceptions.
 */

import axios, {
    AxiosError,
    CanceledError,
    type AxiosInstance,
    type AxiosRequestHeaders,
    type InternalAxiosRequestConfig,
} from 'axios'
import qs from 'qs'
import { getCsrfToken } from '@/helpers/auth'

type EnhancedAxionRequestConfig = InternalAxiosRequestConfig & {
    xsrfHeaderName?: string
    headers?: { [key: string]: any }
}

const apiUrls = {
    apiBase: '',
    appBase: '',
}

let _instance: AxiosInstance | null = null

/**
 * Interceptor for the CSRF token: Each request
 * is enhanced with the CSRF token stored in localStorage
 */
export const csrfInterceptor = (config: EnhancedAxionRequestConfig): EnhancedAxionRequestConfig => {
    const token = getCsrfToken()
    if (!config.headers) {
        config.headers = {} as AxiosRequestHeaders
    }
    if (token) {
        config.headers[config.xsrfHeaderName ?? ''] = getCsrfToken()
    } else {
        delete config.headers[config.xsrfHeaderName ?? '']
    }
    return config
}

export const sessionTimeoutInterceptor = (error: AxiosError) => {
    // Ignore axios cancellations:
    if (error instanceof CanceledError) {
        return
    }
    // ... or else!
    if (error.response?.status === 401) {
        window.alert('Die Session ist abgelaufen, bitte erneut anmelden.')
        const split = window.location.href.split('#')
        if (split.length === 2) {
            window.location.href =
                split[0] + '#/login' + '?redirect=' + encodeURIComponent(split[1])
            window.location.reload();
        }
    } else if (
        error.response?.status === 503 &&
        (error?.response?.data as any)?.error?.msg === 'Maintenance Mode on'
    ) {
        window.location.href =
            'maintenance.html?customer=' + (error?.response?.data as any)?.error?.customer
        window.location.reload();
    } else {
        throw error
    }
}

axios.defaults.xsrfHeaderName = 'X-CSRF-Token'
// axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
axios.interceptors.request.use(csrfInterceptor)
axios.interceptors.response.use((response) => response, sessionTimeoutInterceptor)

export async function loadAuthInfo() {
    try {
        const res = await instance().get('/auth/sessionInfo')
        return res.data
    } catch (err: any) {
        console.error(err.toString())
        return {}
    }
}

export function init(apiBase: string, appBase: string) {
    apiUrls.apiBase = apiBase
    apiUrls.appBase = appBase

    _instance = axios.create({
        baseURL: apiBase,
        timeout: 60000,
    })
    _instance.interceptors.request.use(csrfInterceptor)
    _instance.interceptors.response.use((response) => response, sessionTimeoutInterceptor)
    return _instance
}

export function instance() {
    if (!_instance) {
        throw new Error('API not initialized. Use init() first.')
    }
    return _instance
}

export function getTokenSource() {
    return axios.CancelToken.source()
}

export function getApiBase() {
    return apiUrls.apiBase
}

export function getAppBase() {
    return apiUrls.appBase
}

export function buildUrl(path: string, query?: string | { [key: string]: any }) {
    let url = getApiBase() + path
    url += url.match(/\?/) ? '&' : '?'
    if (typeof query === 'string') {
        url += query
    } else if (typeof query === 'object') {
        url += Object.keys(query)
            .map((key) => String(key) + '=' + encodeURIComponent(query[key]))
            .join('&')
    }
    return url
}

/**
 * forms a complete backend API url from a relative URL,
 * e.g. relUrl = '/login' => 'https://domain.com/services/login
 * @param {string} relUrl
 * @param {string|object|null} query Query part of the URL, either as string or key/value pairs
 * @returns {string} The fully formed URL
 */
export function apiUrl(relUrl?: string, query?: string | { [key: string]: any } | null) {
    return formUrl(getApiBase(), relUrl ?? '', query ?? null)
}

/**
 * forms a complete Frontend app url from a relative URL,
 * e.g. relUrl = '/locale/de.json' => 'https://domain.com/admin-cockpit/locale/de.json'
 * @param {string} relUrl
 * @param {string|object} query Query part of the URL, either as string or key/value pairs
 * @returns {string} The fully formed URL
 */
export function appUrl(relUrl: string, query?: string | { [key: string]: any | null }) {
    return formUrl(getAppBase(), relUrl, query)
}

function formUrl(baseUrl: string, relUrl: string, query?: string | { [key: string]: any } | null) {
    // remove leading slash:
    relUrl = (relUrl || '').replace(/^\//, '')
    // remove trailing slash:
    const root = baseUrl.replace(/\/$/, '')
    let url = `${root}/#/${relUrl}`

    if (query) {
        url += url.match(/\?/) ? '&' : '?'
        if (typeof query === 'string') {
            url += query
        } else if (typeof query === 'object') {
            url += qs.stringify(query)
        }
    }
    return url
}
